import { DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { takeUntil } from 'rxjs';

import { BaseComponent } from '../../../core/components/base/base.component';
import {
  ReferralNetworkLink,
  canEndLink,
  canRespondToLink,
  describeReferralFailure,
  isPlausibleEmail,
  linkDisplayName,
  linkEndedBy,
  linkIcon,
  linkStatusLabel,
  linkTooltip,
} from '../../../core/models/referral.model';
import { ClipboardService } from '../../../core/services/clipboard.service';
import { FeedbackService } from '../../../core/services/feedback.service';
import { GroupService } from '../../../core/services/group.service';
import { ReferralService } from '../../../core/services/referral.service';
import { ReferralInviteDialogComponent, ReferralInviteDialogData, ReferralInviteDialogResult } from './referral-invite-dialog/referral-invite-dialog.component';
import { ReferralPracticeLinkDialogComponent, ReferralPracticeLinkDialogResult } from './referral-practice-link-dialog/referral-practice-link-dialog.component';

/** What a network entry is, as the Referral network list shows it: its chip colour, icon and filter. */
export type NetworkKind = 'clinician' | 'practice' | 'invitation' | 'request';
type NetworkFilter = 'all' | NetworkKind;

/**
 * The referral network (tasks/referrals.md §5.2), in the MedicAI Network tab: the external clinicians this practice can
 * refer patients to, and (architecture §19) the MedicAI practices it is linked with. Admins only; the server enforces
 * that too. Inviting and entering a code happen in dialogs, like every settings tab in this module.
 *
 * ONE section, Referral network (owner, 2026-09-22; first called Medical professionals): every open link in one chip list, each chip coloured and iconed
 * by its kind, with filter chips to narrow it. An email invitation nobody has accepted is ONE kind, Invitation, whichever
 * way it went: the server does not say whether the address was a practice's or an outside clinician's until they accept.
 * A practice waiting on YOUR answer keeps a row (kind Request), because accepting and declining are two decisions and a
 * chip has room for neither.
 */
@Component({
  selector: 'app-referral-network-settings',
  templateUrl: './referral-network-settings.component.html',
  styleUrls: ['./referral-network-settings.component.scss'],
  providers: [DatePipe],
})
export class ReferralNetworkSettingsComponent extends BaseComponent implements OnInit {
  links: ReferralNetworkLink[] = [];
  practiceLinksEnabled = false;
  /** Only the practice's owner answers another practice's request (owner, 2026-09-22); the server enforces it too. */
  viewerCanRespond = false;
  isLoading = false;
  listError: string | null = null;
  endingLinkId: string | null = null;
  respondingLinkId: string | null = null;
  practiceCode: string | null = null;
  codeBusy = false;
  /** The section opens by itself ONCE, on the first load that finds a practice waiting on an answer. */
  sectionExpanded = false;
  private sectionAutoOpened = false;
  /** Which kind the list shows, and a search once the list is long enough to need one. */
  filter: NetworkFilter = 'all';
  query = '';
  readonly searchFrom = 8;

  readonly kindLabel: Record<NetworkKind, string> = { clinician: 'Clinician', practice: 'Practice', invitation: 'Invitation', request: 'Request' };
  /** Each kind its own icon as well as its colour, so colour is never the only difference. */
  readonly kindIcon: Record<NetworkKind, string> = { clinician: 'medical_services', practice: 'domain', invitation: 'schedule_send', request: 'mark_email_unread' };
  private readonly kindOrder: NetworkKind[] = ['request', 'clinician', 'practice', 'invitation'];
  private readonly filterLabel: Record<NetworkFilter, string> = {
    all: 'All', clinician: 'Clinicians', practice: 'Practices', invitation: 'Invitations', request: 'Requests',
  };

  readonly statusLabel = linkStatusLabel;
  readonly endedBy = linkEndedBy;
  readonly displayName = linkDisplayName;
  readonly canEnd = canEndLink;
  readonly canRespond = canRespondToLink;
  readonly iconFor = linkIcon;

  constructor(
    private referralService: ReferralService,
    private groupService: GroupService,
    private clipboard: ClipboardService,
    private datePipe: DatePipe,
    fs: FeedbackService,
    md: MatDialog,
  ) {
    super(fs, md);
  }

  ngOnInit(): void {
    this.groupService.selectedGroup$.pipe(takeUntil(this.destroy$)).subscribe((group: any) => {
      if (group) this.load();
    });
  }

  /**
   * An Invitation is an email nobody has accepted yet, whichever kind it became (the server says only 'invitation'); a
   * Request is another practice waiting on this practice's answer; the rest are what this practice is linked with.
   */
  kindOf(link: ReferralNetworkLink): NetworkKind {
    if (link.targetType === 'invitation') return 'invitation';
    if (this.canRespond(link)) return 'request';
    return link.targetType === 'group' ? 'practice' : 'clinician';
  }

  /** Every link still open (active, or waiting on someone), in kind order and then by name. */
  get openItems(): ReferralNetworkLink[] {
    return this.links.filter((l) => l.status === 'active' || l.status === 'pending')
      .sort((a, b) => this.kindOrder.indexOf(this.kindOf(a)) - this.kindOrder.indexOf(this.kindOf(b))
        || this.displayName(a).localeCompare(this.displayName(b)));
  }

  /** Practices waiting on this practice to accept or decline: a row each, above the chips, and counted in the title. */
  get practiceRequests(): ReferralNetworkLink[] {
    return this.openItems.filter((l) => this.kindOf(l) === 'request');
  }

  /** The chosen kind, or All once that kind has nothing left in it (its last entry was answered or ended). */
  get activeFilter(): NetworkFilter {
    return this.filter === 'all' || this.openItems.some((l) => this.kindOf(l) === this.filter) ? this.filter : 'all';
  }

  get activeFilterLabel(): string {
    return this.activeFilter === 'all' ? 'Referral network' : this.filterLabel[this.activeFilter];
  }

  /** All, then each kind that has something in it, with its count. */
  get filterOptions(): { key: NetworkFilter; label: string; count: number }[] {
    const open = this.openItems;
    const kinds = this.kindOrder
      .map((key) => ({ key, label: this.filterLabel[key], count: open.filter((l) => this.kindOf(l) === key).length }))
      .filter((f) => f.count > 0);
    return [{ key: 'all' as NetworkFilter, label: 'All', count: open.length }, ...kinds];
  }

  private get visibleItems(): ReferralNetworkLink[] {
    const f = this.activeFilter;
    const q = this.query.trim().toLowerCase();
    return this.openItems.filter((l) => (f === 'all' || this.kindOf(l) === f) && (!q || this.searchText(l).includes(q)));
  }

  get visibleRequests(): ReferralNetworkLink[] {
    return this.visibleItems.filter((l) => this.kindOf(l) === 'request');
  }

  get visibleChips(): ReferralNetworkLink[] {
    return this.visibleItems.filter((l) => this.kindOf(l) !== 'request');
  }

  setFilter(value: NetworkFilter | undefined): void {
    // Tapping the chosen chip again deselects it: back to All.
    this.filter = value || 'all';
  }

  clearFilters(event?: Event): void {
    event?.preventDefault();
    this.filter = 'all';
    this.query = '';
  }

  trackByKey(_index: number, option: { key: string }): string {
    return option.key;
  }

  endLabel(link: ReferralNetworkLink): string {
    if (this.kindOf(link) === 'invitation') return `Cancel invitation to ${this.displayName(link)}`;
    return link.status === 'pending' ? `Cancel request to ${this.displayName(link)}` : `End link with ${this.displayName(link)}`;
  }

  private searchText(link: ReferralNetworkLink): string {
    const c = link.clinician as { speciality?: string | null; practiceName?: string | null } | null;
    return [this.displayName(link), link.inviteeEmail, link.inviteeName, c?.speciality, c?.practiceName, link.practice?.name]
      .filter(Boolean).join(' ').toLowerCase();
  }

  /** A chip says who; its tooltip says its kind, then speciality or email, the state, and since when. */
  tooltipFor(link: ReferralNetworkLink): string {
    const details = linkTooltip(link, (iso) => this.datePipe.transform(iso, 'd MMM y') || '');
    return link.status === 'active' || link.status === 'pending' ? `${this.kindLabel[this.kindOf(link)]}\n${details}` : details;
  }

  get closedLinks(): ReferralNetworkLink[] {
    return this.links.filter((l) => l.status === 'declined' || l.status === 'ended');
  }

  trackById(_index: number, link: ReferralNetworkLink): string {
    return link.id;
  }

  load(): void {
    this.isLoading = true;
    this.listError = null;
    this.referralService.listNetwork().pipe(takeUntil(this.destroy$)).subscribe({
      next: ({ links, practiceLinksEnabled, canRespond }) => {
        this.links = links;
        this.practiceLinksEnabled = practiceLinksEnabled;
        this.viewerCanRespond = canRespond;
        this.isLoading = false;
        // Once only: a reload after answering must not close the panel under the user, or reopen one they closed.
        if (!this.sectionAutoOpened && this.practiceRequests.length) {
          this.sectionAutoOpened = true;
          this.sectionExpanded = true;
        }
      },
      error: (error) => {
        this.links = [];
        this.isLoading = false;
        this.listError = describeReferralFailure(error, 'Your referral network could not be loaded.');
      },
    });
  }

  openInviteDialog(event?: Event): void {
    event?.stopPropagation();
    const ref = this.dialog.open<ReferralInviteDialogComponent, ReferralInviteDialogData, ReferralInviteDialogResult>(ReferralInviteDialogComponent, {
      width: '520px', data: { practiceLinksEnabled: this.practiceLinksEnabled },
    });
    ref.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
      if (!result?.invited) return;
      // The same words whichever way it went: the server never says which (tasks/referral-invite-unified.md).
      this.showToastMessage(result.alreadyInNetwork
        ? 'They are already in your referral network, or have been asked.'
        : `Invitation sent to ${result.sentTo}. They will show as connected once they accept.`);
      this.load();
    });
  }

  openPracticeLinkDialog(event?: Event): void {
    event?.stopPropagation();
    const ref = this.dialog.open<ReferralPracticeLinkDialogComponent, void, ReferralPracticeLinkDialogResult>(ReferralPracticeLinkDialogComponent, { width: '480px' });
    ref.afterClosed().pipe(takeUntil(this.destroy$)).subscribe((result) => {
      if (!result?.requested) return;
      this.showToastMessage(result.awaitingOwner ? 'They have asked to link with you. Your practice owner can accept it from their Inbox.'
        : result.alreadyInNetwork ? 'You are already linked with this practice, or have asked them.'
          : result.acceptedTheirRequest ? 'They had asked to link with you: you are now linked.'
            : 'Request sent. The practice will show as linked once their practice owner accepts.');
      this.load();
    });
  }

  showPracticeCode(rotate = false): void {
    this.codeBusy = true;
    (rotate ? this.referralService.rotatePracticeCode() : this.referralService.getPracticeCode()).pipe(takeUntil(this.destroy$)).subscribe({
      next: ({ code }) => {
        this.codeBusy = false;
        this.practiceCode = code;
        if (rotate) this.showToastMessage('New referral code made. The old code no longer works; existing links are unaffected.');
      },
      error: (error) => {
        this.codeBusy = false;
        this.showToastMessage(describeReferralFailure(error, 'The referral code could not be loaded.'));
      },
    });
  }

  async renewPracticeCode(): Promise<void> {
    const confirmed = await this.showConfirmationDialog('NEW REFERRAL CODE',
      'Make a new referral code? The current code stops working at once. Practices you are already linked with are not affected.',
      undefined, 'Cancel', 'Make new code');
    if (confirmed) this.showPracticeCode(true);
  }

  /** Emailing the code to a practice already on MedicAI (owner, 2026-09-18). */
  codeEmail = '';
  showCodeEmail = false;
  emailingCode = false;

  emailPracticeCode(): void {
    const email = this.codeEmail.trim();
    if (!isPlausibleEmail(email)) {
      this.showToastMessage('Enter a valid email address.');
      return;
    }
    this.emailingCode = true;
    this.referralService.emailPracticeCode(email).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.emailingCode = false;
        this.showCodeEmail = false;
        this.codeEmail = '';
        this.showToastMessage(`Your referral code was emailed to ${email}.`);
      },
      error: (error) => {
        this.emailingCode = false;
        this.showToastMessage(describeReferralFailure(error, 'The code could not be emailed. Please try again.'));
      },
    });
  }

  async copyPracticeCode(): Promise<void> {
    if (!this.practiceCode) return;
    const copied = await this.clipboard.copyText(this.practiceCode);
    this.showToastMessage(copied ? 'Referral code copied.' : 'The code could not be copied; select it and copy it instead.');
  }

  async respond(link: ReferralNetworkLink, response: 'accept' | 'decline'): Promise<void> {
    if (response === 'decline') {
      const confirmed = await this.showConfirmationDialog('DECLINE LINK',
        `Decline the link request from ${this.displayName(link)}? They can ask again later.`, undefined, 'Cancel', 'Decline');
      if (!confirmed) return;
    }
    this.respondingLinkId = link.id;
    this.referralService.respondPracticeLink(link.id, response).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.respondingLinkId = null;
        this.showToastMessage(response === 'accept' ? `Linked with ${this.displayName(link)}.` : 'Request declined.');
        this.load();
      },
      error: (error) => {
        this.respondingLinkId = null;
        this.showToastMessage(describeReferralFailure(error, 'The request could not be answered. Please try again.'));
      },
    });
  }

  async endLink(link: ReferralNetworkLink): Promise<void> {
    const confirmed = await this.showConfirmationDialog(
      'END REFERRAL LINK',
      link.targetType === 'invitation'
        ? `Cancel the invitation to ${this.displayName(link)}? Patients you referred to them while it waited are not sent.`
        : link.targetType === 'group'
          ? `End the link with ${this.displayName(link)}? Sharing stops, both ways, for every patient referred over it. Records already shared stay where they are.`
          : `Stop referring to ${this.displayName(link)}? Sharing stops for every patient referred to them. Records already shared stay with them.`,
      undefined, link.targetType === 'invitation' ? 'Keep it' : 'Cancel', link.targetType === 'invitation' ? 'Cancel invitation' : 'End link');
    if (!confirmed) return;
    this.endingLinkId = link.id;
    this.referralService.endLink(link.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.endingLinkId = null;
        this.showToastMessage(link.targetType === 'invitation' ? 'Invitation cancelled.' : 'Referral link ended.');
        this.load();
      },
      error: (error) => {
        this.endingLinkId = null;
        this.showToastMessage(describeReferralFailure(error, 'The link could not be ended. Please try again.'));
      },
    });
  }
}
